import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  MaterialUICore,
  Paper,
  Button,
  MaterialIcons,
} from '@iclinic/design-system';

import { TransactionType } from 'features/onlinePayment/state/constants';
import {
  selectTransactionType,
  selectedPageNumber,
  showWaitingPayments,
} from 'features/onlinePayment/state/reports/transactions';
import {
  getSelectedTransactionType,
  getIsAttemptTransactions,
} from 'features/onlinePayment/state/reports/transactions/selectors';
import useStyles from './Tabs.styles';
import { useEventTracking } from 'shared/hooks/useEventTracking';
import { EVENT_TRACKING } from 'features/onlinePayment/constants';

const { DateRange: IconDateRange, Link: IconLink } = MaterialIcons;
const { AppBar, Tabs: MaterialTabs, Tab } = MaterialUICore;

const tabAccessibilityLabel = (id: string) => `form-payment-tab-${id}`;
const tabPanelAccessibilityLabel = (id: string) =>
  `form-payment-tabpanel-${id}`;

function tabAccessibilityProps(id: string) {
  return {
    id: tabAccessibilityLabel(id),
    'data-testid': tabAccessibilityLabel(id),
    'aria-controls': tabPanelAccessibilityLabel(id),
  };
}

export default function Tabs() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const selectedTransactionType = useSelector(getSelectedTransactionType);
  const isAttemptTransactions = useSelector(getIsAttemptTransactions);
  const eventTracking = useEventTracking();

  const handleChange = (
    _event: React.ChangeEvent<{}>,
    value: TransactionType,
    waitingPayments: boolean = false,
  ) => {
    dispatch(selectTransactionType(value));
    dispatch(showWaitingPayments(waitingPayments));
    dispatch(selectedPageNumber(1));
  };

  const trackAndChangeTab = (
    event: React.ChangeEvent<{}>,
    value: TransactionType,
    buttonName: string,
    waitingPayments = false,
  ) => {
    handleChange(event, value, waitingPayments);
    eventTracking('button_click', {
      app_id: EVENT_TRACKING.appId,
      app_feature: EVENT_TRACKING.appFeature,
      button_name: buttonName,
      button_type: 'button',
    });
  };

  const handleClickProcessedTransactions = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    trackAndChangeTab(
      event,
      TransactionType.Online,
      'payment_history-processed_transactions',
      false,
    );
  };

  const handleClickPendingPayment = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    trackAndChangeTab(
      event,
      TransactionType.Online,
      'payment_history-pending_payment',
      true,
    );
  };

  const handleTabChange = (
    event: React.ChangeEvent<{}>,
    value: TransactionType,
  ) => {
    const buttonName =
      value === TransactionType.Online
        ? 'payment_history-payment_link'
        : 'payment_history-payment_terminal';

    trackAndChangeTab(event, value, buttonName, false);
  };

  return (
    <Paper variant="outlined" className={classes.tabsHeader}>
      <AppBar position="static" color="transparent" elevation={0}>
        <MaterialTabs
          className={classes.tabs}
          aria-label="Formas de pagamento"
          textColor="primary"
          indicatorColor="primary"
          value={selectedTransactionType}
          onChange={handleTabChange}
        >
          <Tab
            value={TransactionType.Online}
            label="Pagamento Online"
            {...tabAccessibilityProps('pagamento-online')}
          />
          <Tab
            value={TransactionType.Presential}
            label="Maquininha"
            {...tabAccessibilityProps('maquininha')}
          />
        </MaterialTabs>
      </AppBar>
      {selectedTransactionType === TransactionType.Online && (
        <div className={classes.subTabHeader}>
          <Button
            variant="text"
            className={`${classes.subTabButton} ${
              isAttemptTransactions ? 'selected' : ''
            }`}
            {...tabAccessibilityProps('transacoes-efetuadas')}
            onClick={handleClickProcessedTransactions}
          >
            <IconDateRange /> Transações efetuadas
          </Button>
          <Button
            variant="text"
            className={`${classes.subTabButton} ${
              !isAttemptTransactions ? 'selected' : ''
            }`}
            {...tabAccessibilityProps('aguardando-pagamento')}
            onClick={handleClickPendingPayment}
          >
            <IconLink /> Aguardando Pagamento
          </Button>
        </div>
      )}
    </Paper>
  );
}
